import { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getStacksScore,
  submitStacksScore,
  getStacksLeaderboard,
} from "../util/wordywhiz";
import { loadDictionary, isValidWord as isValidStacksWord } from "../stacks/dictionary";
import { getLocalHighScore, setLocalHighScore } from "../stacks/localScore";
import { track } from "../../../lib/analytics";
import {
  createEmptyGrid,
  dropLetter,
  findMatches,
  clearAndCollapse,
  scoreForMatches,
  isColumnFull,
  isBoardFull,
} from "../stacks/engine";
import { getMode, pickRandomLetter } from "../stacks/constants";
import { buildDrillMode } from "../stacks/drill";
import { isStrictMode } from "../stacks/ModeIcon";
import AdSlot from "../../AdSlot";

const FLASH_MS = 450;

function StacksGame({ mode }) {
  const { isLoggedIn } = useAuth();
  const { cols: COLS, rows: ROWS, matchStrategy } = mode;
  const isValidWord = mode.wordSet ? (word) => mode.wordSet.has(word) : isValidStacksWord;

  const [dictReady, setDictReady] = useState(false);
  const [dictError, setDictError] = useState(false);

  const [grid, setGrid] = useState(() => createEmptyGrid(ROWS, COLS));
  const [currentLetter, setCurrentLetter] = useState(() => pickRandomLetter(mode.letterWeights));
  const [flashCells, setFlashCells] = useState([]);
  const [busy, setBusy] = useState(false);
  const [gameOver, setGameOver] = useState(false);

  const [score, setScore] = useState(0);
  // The clear badge (§4.4) — set exactly while a match's flash is playing,
  // so its lifetime is tied to the actual clear rather than an independent timer.
  const [clearBadge, setClearBadge] = useState(null); // { words: string[], points: number }

  const [highScore, setHighScore] = useState(null);
  const [localHighScore, setLocalHighScoreState] = useState(() =>
    mode.persistsScore ? getLocalHighScore(mode.id) : null
  );
  const [isNewHigh, setIsNewHigh] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);

  const bestScore = isLoggedIn ? highScore : localHighScore;

  useEffect(() => {
    // Drill mode already has its dictionary in memory (it came from the
    // chosen list, not a fetch) — only the other modes need to load the
    // general Stacks word list.
    if (mode.wordSet) {
      setDictReady(true);
    } else {
      loadDictionary()
        .then(() => setDictReady(true))
        .catch(() => setDictError(true));
    }
    track("stacks_game_started", { mode: mode.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mode.persistsScore) return;
    if (isLoggedIn) {
      getStacksScore(mode.id).then((data) => setHighScore(data.high_score)).catch(() => {});
    }
    getStacksLeaderboard(mode.id).then(setLeaderboard).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const endGame = useCallback(async (finalScore) => {
    setGameOver(true);
    track("stacks_game_over", { mode: mode.id, score: finalScore });

    if (!mode.persistsScore) return;

    if (isLoggedIn) {
      try {
        const res = await submitStacksScore(mode.id, finalScore);
        setHighScore(res.high_score);
        setIsNewHigh(res.is_new_high);
        getStacksLeaderboard(mode.id).then(setLeaderboard).catch(() => {});
      } catch {
        // score just won't sync this time — not worth blocking the game-over screen over
      }
    } else {
      const isNew = finalScore > localHighScore;
      if (isNew) {
        setLocalHighScore(mode.id, finalScore);
        setLocalHighScoreState(finalScore);
      }
      setIsNewHigh(isNew);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, localHighScore, mode.id, mode.persistsScore]);

  function handleDrop(col) {
    if (gameOver || busy || !dictReady || isColumnFull(grid, col)) return;

    const dropped = dropLetter(grid, col, currentLetter);
    if (!dropped) return;

    const matches = findMatches(dropped.grid, dropped.row, dropped.col, isValidWord, matchStrategy, mode.minWordLen);
    setGrid(dropped.grid);

    if (matches.length === 0) {
      if (isBoardFull(dropped.grid)) {
        endGame(score);
      } else {
        setCurrentLetter(pickRandomLetter(mode.letterWeights));
      }
      return;
    }

    // Flash the matched word(s) briefly before clearing + letting the stack settle.
    const cellsToClear = matches.flatMap((m) => m.cells);
    const scoreDelta = scoreForMatches(matches);
    const finalScore = score + scoreDelta;

    setBusy(true);
    setFlashCells(cellsToClear);
    setClearBadge({ words: matches.map((m) => m.word), points: scoreDelta });
    matches.forEach((m) => track("stacks_word_cleared", { mode: mode.id, word: m.word, direction: m.direction }));

    setTimeout(() => {
      const collapsed = clearAndCollapse(dropped.grid, cellsToClear);
      setGrid(collapsed);
      setFlashCells([]);
      setClearBadge(null);
      setScore(finalScore);
      setBusy(false);
      if (isBoardFull(collapsed)) {
        endGame(finalScore);
      } else {
        setCurrentLetter(pickRandomLetter(mode.letterWeights));
      }
    }, FLASH_MS);
  }

  function newGame() {
    setGrid(createEmptyGrid(ROWS, COLS));
    setCurrentLetter(pickRandomLetter(mode.letterWeights));
    setFlashCells([]);
    setClearBadge(null);
    setBusy(false);
    setGameOver(false);
    setScore(0);
    setIsNewHigh(false);
    track("stacks_game_started", { mode: mode.id });
  }

  const isFlashed = (row, col) => flashCells.some((c) => c.row === row && c.col === col);

  return (
    <div className="stacks-page">
      <div className="stacks-header">
        <h1 className="ww-label ww-label--on-walnut">
          Wordy Whiz · {mode.name}
          {" · "}
          <Link to={mode.changeModeHref || "/wordy-whiz/play"} className="stacks-change-mode">
            {mode.changeModeLabel || "Change mode"}
          </Link>
        </h1>
        {isStrictMode(mode) && <p className="ww-label ww-label--on-walnut">Strict · No partial credit</p>}
      </div>

      <div className="stacks-hud">
        <div className="stacks-hud__item">
          <span className="ww-label ww-label--on-walnut">Score</span>
          <span className="stacks-hud__score">{score}</span>
        </div>
        {mode.persistsScore && (
          <div className="stacks-hud__item">
            <span className="ww-label ww-label--on-walnut">Best</span>
            <span className="stacks-hud__number">{bestScore || "—"}</span>
          </div>
        )}
        <div className="stacks-hud__item">
          <span className="ww-label ww-label--on-walnut">Next</span>
          <span className="ww-tile ww-tile--sm">{dictReady ? currentLetter : "…"}</span>
        </div>
      </div>

      {!isLoggedIn && mode.persistsScore && (
        <p className="stacks-login-hint">
          Playing as a guest. Your best is saved on this device. Log in to join the leaderboards.
        </p>
      )}

      {dictError && (
        <p className="stacks-error">
          Couldn't load the word list. Refresh to try again.
        </p>
      )}

      <div className="stacks-board" aria-disabled={!dictReady || gameOver}>
        {clearBadge && (
          <div className="stacks-clear-badge">
            <span className="stacks-clear-badge__word">{clearBadge.words.join("  +  ")}</span>
            <span className="stacks-clear-badge__points">+{clearBadge.points}</span>
          </div>
        )}

        <div
          className="stacks-grid"
          style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}
        >
          {Array.from({ length: COLS }, (_, col) => (
            <button
              key={col}
              className="stacks-column"
              disabled={!dictReady || busy || gameOver || isColumnFull(grid, col)}
              onClick={() => handleDrop(col)}
              aria-label={`Drop ${currentLetter} in column ${col + 1}`}
            >
              {Array.from({ length: ROWS }, (_, r) => ROWS - 1 - r).map((row) => {
                const letter = grid[row][col];
                const cls = [
                  "stacks-cell",
                  letter ? "filled" : "",
                  isFlashed(row, col) ? "flash" : "",
                ].filter(Boolean).join(" ");
                return (
                  <span key={row} className={cls}>
                    {letter}
                  </span>
                );
              })}
            </button>
          ))}
        </div>

        {gameOver && (
          <div className="stacks-gameover">
            <p className="stacks-gameover__title">
              {isNewHigh ? "New high score!" : "Game over"}
            </p>
            <p className="stacks-gameover__score">{score} pts</p>
            <button className="ww-btn ww-btn--accent" onClick={newGame}>Play again</button>
          </div>
        )}
      </div>

      {!gameOver && <p className="stacks-hint ww-label ww-label--on-walnut">Tap a column to drop</p>}

      {mode.persistsScore && leaderboard.length > 0 && (
        <div className="stacks-leaderboard">
          <p className="ww-label ww-label--on-walnut">{mode.name} · Top scores</p>
          <ol>
            {leaderboard.map((row, i) => (
              <li key={i}>
                <span className="stacks-leaderboard__rank">{i + 1}</span>
                <span className="stacks-leaderboard__name">{row.username}</span>
                <span className="stacks-leaderboard__score">{row.high_score}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <AdSlot slot={process.env.REACT_APP_ADSENSE_SLOT_STACKS} className="mt-4" />
    </div>
  );
}

function NoDrillList() {
  const navigate = useNavigate();
  return (
    <div className="stacks-page">
      <div className="stacks-header">
        <p className="ww-label ww-label--on-walnut">Wordy Whiz · Play</p>
      </div>
      <div className="stacks-empty">
        <h1 className="stacks-empty__title">No word list selected</h1>
        <p className="stacks-empty__body">Drill practices spelling from a dictionary you're studying. Pick one to start.</p>
        <button className="ww-btn ww-btn--accent" onClick={() => navigate("/wordy-whiz/dictionaries")}>
          Browse Dictionaries
        </button>
      </div>
    </div>
  );
}

/** Reads the mode from the route and remounts StacksGame fresh whenever it changes. */
function Stacks() {
  const { mode: modeId } = useParams();
  const location = useLocation();

  if (modeId === "drill") {
    const config = location.state;
    if (!config || !config.words || config.words.length === 0) {
      return <NoDrillList />;
    }
    const mode = buildDrillMode(config);
    // Remount on a config change (a different list, min length, or ruleset)
    // rather than reusing an in-progress board from the previous setup.
    const key = `drill:${mode.dictTitle}:${mode.matchStrategy}:${mode.minWordLen}`;
    return <StacksGame key={key} mode={mode} />;
  }

  const mode = getMode(modeId);
  return <StacksGame key={mode.id} mode={mode} />;
}

export default Stacks;
